import { FC, useState } from 'react'
import { Skeleton } from 'src/components/Skeleton'
import { useGetTopIntentsQuery } from 'src/reactQuery/useGetTopIntentsQuery'
import { IntentDetailView } from './IntentDetailView'
import {
  IntentRow,
  SEGMENT_META,
  SegmentKey,
  SentimentBar,
  TruncatedLabel,
  dominantColor,
} from './parts'
import classes from './topCustomerIntents.module.scss'

// From topCustomerIntents.module.scss: .tableRow is a fixed 72px, .intentDot is
// 10px square with radius 3, .sentimentBar is a 6px-gap column of an 8px
// .barTrack plus a 12px/1.4 (17px) .barLabels line, and .totalCount is 16px.
// The query asks for 8 intents (limit below) and 8 * 72 = 576 is exactly the
// body's height budget (.tableCard max-height 640 - .tableHeader 64), so the
// card fills without scrolling.
const INTENT_LIMIT = 8
const DOT_SIZE = 10
const DOT_RADIUS = 3
const BAR_TRACK_HEIGHT = 8
const BAR_TRACK_RADIUS = 20
const BAR_LABEL_HEIGHT = 17
const TOTAL_COUNT_HEIGHT = 16

interface TopCustomerIntentsProps {
  fromDate?: string
  pageId: string
  rows?: IntentRow[]
  toDate?: string
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const TopCustomerIntents: FC<TopCustomerIntentsProps> = ({
  /* eslint-disable @typescript-eslint/no-unused-vars */
  rows: _externalRows,
  /* eslint-enable @typescript-eslint/no-unused-vars */
  pageId,
  fromDate,
  toDate,
}) => {
  const [selectedIntent, setSelectedIntent] = useState<IntentRow | null>(null)

  const { data: apiData, isLoading } = useGetTopIntentsQuery({
    fromDate,
    limit: INTENT_LIMIT,
    pageId,
    toDate,
  })

  // `rows` is [] until the response lands, so without this the table rendered
  // its column header above an empty body — the card collapsed to the 64px
  // header and then jumped open to full height. First load only: a refetch
  // keeps the existing rows on screen.
  const isInitialLoading = isLoading && !apiData

  const rows: IntentRow[] = (apiData?.intents || []).map((intent) => ({
    id: intent.canonicalId || intent.id,
    name: intent.intent,
    negative: intent.negativeMentions,
    neutral: intent.neutralMentions,
    positive: intent.positiveMentions,
    total: intent.totalMentions,
  }))

  return (
    <div className={classes.container}>
      {/* ── Widget header (always visible) ── */}
      <div className={classes.header}>
        <h2 className={classes.title}>Top Customer Intents</h2>
        <div className={classes.legend}>
          {(['negative', 'neutral', 'positive'] as SegmentKey[]).map(
            (segment) => (
              <div className={classes.legendItem} key={segment}>
                <span
                  className={classes.legendDot}
                  style={{ background: SEGMENT_META[segment].color }}
                />
                <span className={classes.legendLabel}>
                  {SEGMENT_META[segment].label}
                </span>
              </div>
            )
          )}
        </div>
      </div>

      {selectedIntent ? (
        <IntentDetailView
          fromDate={fromDate}
          intent={selectedIntent}
          onBack={() => setSelectedIntent(null)}
          pageId={pageId}
          toDate={toDate}
        />
      ) : (
        <div className={classes.tableCard}>
          {/* column header */}
          <div className={classes.tableHeader}>
            <span className={classes.colIntent}>Intent</span>
            <span className={classes.colBreakdown}>Sentiment Breakdown</span>
            <span className={classes.colTotal}>Total Messages</span>
          </div>

          {/* scrollable body */}
          <div className={classes.tableBody}>
            {isInitialLoading &&
              Array.from({ length: INTENT_LIMIT }).map((_, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <div className={classes.tableRow} key={index}>
                  <div className={classes.intentCell}>
                    <Skeleton
                      height={DOT_SIZE}
                      radius={DOT_RADIUS}
                      width={DOT_SIZE}
                    />
                    <Skeleton height={16} width="70%" />
                  </div>

                  <div className={classes.breakdownCell}>
                    <div className={classes.sentimentBar}>
                      <Skeleton
                        height={BAR_TRACK_HEIGHT}
                        radius={BAR_TRACK_RADIUS}
                        width="100%"
                      />
                      <div className={classes.barLabels}>
                        <Skeleton height={BAR_LABEL_HEIGHT} width={72} />
                        <Skeleton height={BAR_LABEL_HEIGHT} width={64} />
                        <Skeleton height={BAR_LABEL_HEIGHT} width={72} />
                      </div>
                    </div>
                  </div>

                  <div className={classes.totalCell}>
                    <Skeleton height={TOTAL_COUNT_HEIGHT} width={36} />
                  </div>
                </div>
              ))}
            {rows.map((row) => (
              <div
                className={`${classes.tableRow} ${classes.clickableRow}`}
                key={row.id}
                onClick={() => setSelectedIntent(row)}
                role="button"
                tabIndex={0}
              >
                {/* intent cell */}
                <div className={classes.intentCell}>
                  <span
                    className={classes.intentDot}
                    style={{ background: dominantColor(row) }}
                  />
                  <TruncatedLabel text={row.name} />
                </div>

                {/* sentiment breakdown cell */}
                <div className={classes.breakdownCell}>
                  <SentimentBar row={row} />
                </div>

                {/* total messages cell */}
                <div className={classes.totalCell}>
                  <span className={classes.totalCount}>{row.total}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
